import React from "react";
import {Col} from "react-bootstrap";
import PropTypes from "prop-types";
import {GSPSMediaSelector} from "Components/Common/GSPSMediaSelector";
import {GSPSIcon} from "Icons/GSPSIcon";
import {insertNotification} from "Utils/NotificationsUtils";
import {StyledMediaContainer, StyledMediaInput} from "Pages/Audits/AuditMedia.styles";
import {convertToEstDate} from "Services/TimeZonesUtils";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

export const initMediaSelector = (elementType, elementId, mediaId, mediaValue) => {
    return {elementType: elementType, elementId: elementId, mediaId: mediaId, mediaValue: mediaValue};
};

export const AuditMedia = ({
    rowMedia,
    updateElementMedia,
    elementType,
    mediaWidth,
    mediaHeight,
    deleteMedia,
    deleteMarginWidth = 20,
    limit = 100,
    mediaType = "image",
    isViewOnly = true,
    isShowNotes = true,
}) => {
    const [newNotes, setNewNotes] = React.useState(
        rowMedia.map((media, index) => ({index, notes: media?.notes})),
    );

    const sliderSettings = {
        dots: true, // Add dots for navigation
        infinite: false, // No infinite looping
        speed: 500,
        slidesToShow: 3, // Show up to 3 items
        slidesToScroll: 1,
        arrows: true, // Show navigation arrows
        dotsClass: "slick-dots custom-dots",
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 2, // Show 2 items on tablets
                    slidesToScroll: 1,
                },
            },
            {
                breakpoint: 480,
                settings: {
                    slidesToShow: 1, // Show 1 item on mobile
                    slidesToScroll: 1,
                },
            },
        ],
    };

    const mediaIdFromIndex = (issueIndex, mediaId) => {
        return `${mediaType}_${elementType}_${issueIndex}_${mediaId}`;
    };

    const handleChangeImage = (event, index) => {
        event.preventDefault();
        const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB in bytes
        const file = event.target.files[0];
        if (file.size > MAX_FILE_SIZE) {
            insertNotification("Error", "File size exceeds 200MB limit", "error");
            return;
        }

        try {
            onMediaChange(elementType, index, index, event.target.files);
        } catch (e) {
            insertNotification("Error", "Couldn't upload File", "error");
        }
    };

    function getMediaTypesFileFormats(mediaType) {
        if (mediaType === "image") {
            return "image/*,.heic";
        } else if (mediaType === "video") {
            return "video/*";
        }
        console.warn(`Unknown Media type ${mediaType} received in MediaSelector component`);
        return "*";
    }

    const onMediaChange = (elementType, index, mediaId, mediaValue) => {
        if (mediaType === "video") {
            const video = document.createElement("video");
            video.preload = "metadata";
            const file = mediaValue[0];
            const reader = new FileReader();

            reader.onload = () => {
                video.src = reader.result;

                video.onloadedmetadata = () => {
                    if (video.duration > 35) {
                        insertNotification(
                            "Error",
                            `Video duration exceeds the 30-second limit 
                            (Duration: ${Math.ceil(video.duration)} seconds)`,
                            "error",
                        );
                        return;
                    }
                    updateElementMedia(index, mediaValue, elementType);
                };
            };
            reader.onerror = () => {
                insertNotification("Error", "Failed to load video file", "error");
            };
            reader.readAsDataURL(file);
        } else {
            updateElementMedia(index, mediaValue, elementType);
        }
    };

    return (
        <>
            {!isViewOnly && rowMedia.length < limit &&
                <StyledMediaInput>
                    <label htmlFor={mediaType + "Upload"}>
                        <GSPSIcon
                            name={"PlusCircleFill"}
                            role="button"
                            color={getComputedStyle(document.body).getPropertyValue("--bs-primary")}
                        />

                    </label>
                    <input style={{visibility: "hidden", width: "1px"}} accept={getMediaTypesFileFormats(mediaType)}
                        id={mediaType + "Upload"}
                        type={"file"}
                        onChange={(e) => handleChangeImage(e, rowMedia.length)}
                        name={mediaType + "Upload"}
                    />
                </StyledMediaInput>
            }

            <Col>
                <StyledMediaContainer>
                    <div style={{width: "100%", padding: "20px"}}>
                        <Slider {...sliderSettings}>
                            { rowMedia?.length > 0 && rowMedia.map((media, index) =>(
                                <div className="d-inline position-relative" key={`${elementType}_rowmedia_${index}`}>
                                    <div style={{
                                        height: `${isShowNotes ? "300px" : "100px"}`,
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        marginBottom: "16px",
                                    }}>
                                        <GSPSMediaSelector
                                            key={mediaIdFromIndex(media.elementId, media.mediaId)}
                                            controlSize={media.mediaId}
                                            imageContainerSizePx={mediaWidth ? mediaWidth : 300}
                                            imageContainerHeightSizePx={mediaHeight ? mediaHeight : 200}
                                            currentImageSrc={
                                                media[`${mediaType}_value`] ? media[`${mediaType}_value`] :
                                                    media.mediaValue ? media.mediaValue :
                                                        undefined
                                            }
                                            notes={media?.notes || ""}
                                            isViewOnly={isViewOnly}
                                            setValue={(id, value) => {
                                                onMediaChange(elementType, id, media.mediaId, value, media);
                                            }}
                                            onNotesSaved={(value) => {
                                                if (media.mediaValue === undefined) {
                                                    setNewNotes(
                                                        newNotes.map((item) =>
                                                            item.index === index ? {...item, notes: value} : item),
                                                    );
                                                } else {
                                                    setNewNotes([...newNotes, {index, notes: value}]);
                                                    updateElementMedia(index, [media.mediaValue], elementType, value);
                                                }
                                            }}
                                            mediaType={mediaType}
                                            media={media}
                                            elementType={elementType}
                                        />
                                        {newNotes && isShowNotes &&
                                            <p>
                                                {newNotes.filter((item) => item.index === index)[0]?.notes}
                                            </p>
                                        }
                                        {!isViewOnly && <>
                                            <div style={{
                                                display: "flex",
                                                justifyContent: "space-between",
                                                alignItems: "center",
                                                paddingRight: `${deleteMarginWidth}px`,
                                            }}>
                                                {convertToEstDate(media.created_at, true) ||
                                                convertToEstDate(new Date(), true)}
                                                {!isViewOnly &&
                                                    <GSPSIcon
                                                        name="TrashFill"
                                                        color="red"
                                                        size="18"
                                                        role="button"
                                                        className="text-bg-red"
                                                        style={{
                                                            position: "relative",
                                                        }}
                                                        onClick={() => {
                                                            deleteMedia(media, index, mediaType);
                                                        }}>
                                                    </GSPSIcon>
                                                }
                                            </div>
                                        </>}
                                    </div>
                                </div>
                            ))}
                        </Slider>
                    </div>
                </StyledMediaContainer>
            </Col>
        </>
    );
};

AuditMedia.propTypes = {
    rowMedia: PropTypes.array,
    mediaHeight: PropTypes.number,
    mediaWidth: PropTypes.number,
    updateElementMedia: PropTypes.func.isRequired,
    elementType: PropTypes.string,
    isViewOnly: PropTypes.bool,
    limit: PropTypes.number,
    mediaType: PropTypes.oneOf([
        "image",
        "video",
    ]),
    deleteMedia: PropTypes.func,
    isShowNotes: PropTypes.bool,
    deleteMarginWidth: PropTypes.string,
};

export const AuditImages = ({
    rowImages,
    mediaHeight,
    mediaWidth,
    updateElementImages,
    elementType,
    limit,
    isViewOnly = true,
    deleteMedia,
    deleteMarginWidth,
    isShowNotes = true,
}) => {
    return <AuditMedia
        rowMedia={rowImages}
        mediaWidth={mediaWidth}
        mediaHeight={mediaHeight}
        updateElementMedia={updateElementImages}
        elementType={elementType}
        limit={limit}
        isViewOnly={isViewOnly}
        mediaType={"image"}
        isShowNotes={isShowNotes}
        deleteMedia={deleteMedia}
        deleteMarginWidth={deleteMarginWidth}
    />;
};

AuditImages.propTypes = {
    rowImages: PropTypes.array,
    mediaHeight: PropTypes.number,
    mediaWidth: PropTypes.number,
    updateElementImages: PropTypes.func.isRequired,
    deleteMedia: PropTypes.func.isRequired,
    elementType: PropTypes.string,
    isViewOnly: PropTypes.bool,
    isShowNotes: PropTypes.bool,
    limit: PropTypes.number,
    deleteMarginWidth: PropTypes.string,
};

export const AuditVideos = ({
    rowVideos,
    mediaHeight,
    mediaWidth,
    updateElementVideos,
    elementType,
    limit,
    isViewOnly = true,
    deleteMedia,
    deleteMarginWidth,
    isShowNotes = true,
}) => {
    return <AuditMedia
        rowMedia={rowVideos}
        mediaWidth={mediaWidth}
        mediaHeight={mediaHeight}
        updateElementMedia={updateElementVideos}
        elementType={elementType}
        limit={limit}
        isViewOnly={isViewOnly}
        mediaType={"video"}
        isShowNotes={isShowNotes}
        deleteMedia={deleteMedia}
        deleteMarginWidth={deleteMarginWidth}
    />;
};

AuditVideos.propTypes = {
    rowVideos: PropTypes.array,
    mediaHeight: PropTypes.number,
    mediaWidth: PropTypes.number,
    updateElementVideos: PropTypes.func.isRequired,
    deleteMedia: PropTypes.func.isRequired,
    elementType: PropTypes.string,
    isViewOnly: PropTypes.bool,
    isShowNotes: PropTypes.bool,
    limit: PropTypes.number,
    deleteMarginWidth: PropTypes.string,
};
